import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";
import { fetchWikiDocument } from "@/lib/wiki";
import { WikiDocumentMarkdown } from "@/lib/wikiDocumentMarkdown";

export function WikiDocumentPage() {
  const { id } = useParams<{ id: string }>();
  const documentId = id ?? "";
  const documentQuery = useQuery({
    queryKey: ["wiki", "document", documentId],
    queryFn: () => fetchWikiDocument(documentId),
    enabled: documentId.length > 0,
    retry: false,
  });
  const document = documentQuery.data?.document;

  usePageBreadcrumb(document?.title?.trim() || "Wiki page");

  if (documentQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading wiki page...
      </div>
    );
  }

  if (documentQuery.error) {
    const message =
      documentQuery.error instanceof ApiError && documentQuery.error.status === 409
        ? "Outline sign-in is required before viewing this wiki page."
        : documentQuery.error.message;
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
        {message}
      </div>
    );
  }

  if (!document) {
    return <p className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">Wiki page not found.</p>;
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <Link to="/wiki" className="text-sm font-medium text-brand-700 hover:text-brand-800">
            Back to wiki search
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900">{document.title}</h1>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
            {document.collectionName ? <span>{document.collectionName}</span> : null}
            {document.updatedAt ? <span>Updated {new Date(document.updatedAt).toLocaleDateString()}</span> : null}
          </div>
        </div>
        {document.url ? (
          <a
            href={document.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
          >
            Open in Outline
            <ExternalLink className="h-4 w-4" aria-hidden />
          </a>
        ) : null}
      </header>

      <article className="rounded-lg border border-slate-200 bg-white px-6 py-5 shadow-sm">
        <div className="mx-auto max-w-[min(100%,72ch)]">
          <WikiDocumentMarkdown text={document.text} />
        </div>
      </article>
    </div>
  );
}
